import { EmailAddressZ } from "@sellar-io/common";
import {
  Button,
  InputEmail,
  InputOption,
  InputPostalCode,
  InputSelect,
  InputText,
} from "@sellar-io/components";
import type { RyftAccountCreateRequestType } from "@sellar-io/api-v2/types";
import { checkPostcodeValidCharacters } from "@sellar-io/common";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { FormField } from "../onboarding/components/FormField";

type RyftBusinessType = RyftAccountCreateRequestType["type"];

const ryftBusinessTypes = [
  { label: "Corporation", value: "Corporation" },
  { label: "Government entity", value: "GovernmentEntity" },
  { label: "Charity", value: "Charity" },
  { label: "Limited partnership", value: "LimitedPartnership" },
  { label: "Private company", value: "PrivateCompany" },
  { label: "Public company", value: "PublicCompany" },
] satisfies ReadonlyArray<{ label: string; value: RyftBusinessType }>;

const RyftBusinessTypeSchema = z.custom<RyftBusinessType>(
  (value) =>
    ryftBusinessTypes.some((businessType) => businessType.value === value),
  "Business type is required",
);

const RyftBusinessFormSchema = z.object({
  businessName: z.string().trim().min(1, "Business name is required"),
  businessType: z.string().pipe(RyftBusinessTypeSchema),
  companyRegistrationNumber: z
    .string()
    .trim()
    .min(1, "Company registration number is required"),
  addressLine: z.string().trim().min(1, "Address line is required"),
  city: z.string().trim().min(1, "City is required"),
  postalCode: z
    .string()
    .trim()
    .min(1, "Postal code is required")
    .refine(checkPostcodeValidCharacters, "Enter a valid UK postal code"),
  contactEmail: EmailAddressZ,
});

export type RyftBusinessFormValues = z.input<typeof RyftBusinessFormSchema>;

export type RyftBusinessSubmissionValues = z.output<
  typeof RyftBusinessFormSchema
>;

export const RyftEditBusinessForm = ({
  mode,
  initialData,
  isSubmitting,
  disabled = false,
  onSubmit,
}: {
  mode: "create" | "update";
  initialData: RyftBusinessFormValues;
  isSubmitting: boolean;
  disabled?: boolean;
  onSubmit: (values: RyftBusinessSubmissionValues) => void | Promise<void>;
}) => {
  const form = useForm({
    defaultValues: initialData,
    validators: {
      onChange: RyftBusinessFormSchema,
    },
    onSubmit: ({ value }) => {
      if (disabled || isSubmitting) return;
      return onSubmit(RyftBusinessFormSchema.parse(value));
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <fieldset
        className="flex flex-col gap-4 disabled:opacity-60"
        disabled={isSubmitting || disabled}
      >
        <form.Field name="businessName">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Business name"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputText
                value={state.value}
                onBlur={handleBlur}
                onChange={(event) => handleChange(event.target.value)}
                autoComplete="organization"
                required
              />
            </FormField>
          )}
        </form.Field>

        <div className="grid gap-4 md:grid-cols-2">
          <form.Field name="businessType">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Business type"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputSelect
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) =>
                    handleChange(
                      RyftBusinessTypeSchema.parse(event.target.value),
                    )
                  }
                  required
                >
                  {ryftBusinessTypes.map((businessType) => (
                    <InputOption
                      key={businessType.value}
                      value={businessType.value}
                    >
                      {businessType.label}
                    </InputOption>
                  ))}
                </InputSelect>
              </FormField>
            )}
          </form.Field>

          <form.Field name="companyRegistrationNumber">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Company registration number"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  autoComplete="off"
                  required
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <form.Field name="addressLine">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Address line"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputText
                value={state.value}
                onBlur={handleBlur}
                onChange={(event) => handleChange(event.target.value)}
                autoComplete="address-line1"
                required
              />
            </FormField>
          )}
        </form.Field>

        <div className="grid gap-4 md:grid-cols-2">
          <form.Field name="city">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="City"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  autoComplete="address-level2"
                  required
                />
              </FormField>
            )}
          </form.Field>

          <form.Field name="postalCode">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Postal code"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputPostalCode
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  country="GB"
                  required
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <form.Field name="contactEmail">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Contact email"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputEmail
                value={state.value}
                onBlur={handleBlur}
                onChange={(event) => handleChange(event.target.value)}
                autoComplete="email"
                required
              />
            </FormField>
          )}
        </form.Field>

        <div className="flex justify-end">
          <Button
            disabled={isSubmitting || disabled}
            loading={isSubmitting}
            type="submit"
          >
            {mode === "create" ? "Continue" : "Save changes"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};
